import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FallbackTier, NormalizedFeed } from "@chula/shared";
import { feedKeysForPath } from "../lib/feedKeys";
import { subscribeFeedKeys } from "./useLiveStream";

interface FeedState<T> {
  data: T[];
  ageMinutes: number;
  fallbackTier: FallbackTier | "loading";
  loadedAt: string | null;
  error: string | null;
}

const STORAGE_PREFIX = "chula:feed:";
const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000;
const UNAVAILABLE_AFTER_FAILS = 3;
/** Collapse bursts of stream notifications (e.g. /api/executive's four keys) into one refetch. */
export const LIVE_REFETCH_DEBOUNCE_MS = 750;

function storageKey(path: string): string {
  return `${STORAGE_PREFIX}${path}`;
}

function readLocal<T>(path: string): FeedState<T> | null {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(storageKey(path)) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state: FeedState<T>; savedAt: number };
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > MAX_CACHE_AGE_MS) return null;
    const elapsed = parsed.state.loadedAt ? (Date.now() - Date.parse(parsed.state.loadedAt)) / 60_000 : 0;
    return { ...parsed.state, fallbackTier: "cache", ageMinutes: Math.max(parsed.state.ageMinutes, Number.isFinite(elapsed) ? elapsed : 0) };
  } catch {
    return null;
  }
}

function writeLocal<T>(path: string, state: FeedState<T>): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(storageKey(path), JSON.stringify({ state, savedAt: Date.now() }));
  } catch {}
}

const emptyInitial = <T,>(): FeedState<T> => ({
  data: [],
  ageMinutes: 0,
  fallbackTier: "loading",
  loadedAt: null,
  error: null,
});

async function fetchWithRetry(url: string, signal: AbortSignal, retries = 2): Promise<Response> {
  let lastErr: Error | undefined;
  for (let i = 0; i <= retries; i++) {
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    try {
      const res = await fetch(url, { signal, cache: "no-store" });
      if (res.ok) return res;
      if (res.status < 500 && res.status !== 429) throw new Error(`${res.status} ${res.statusText}`);
      lastErr = new Error(`${res.status} ${res.statusText}`);
    } catch (err) {
      lastErr = err as Error;
      if (err instanceof DOMException && err.name === "AbortError") throw err;
    }
    if (i < retries && !signal.aborted) {
      await new Promise((r) => setTimeout(r, 800 * (i + 1)));
    }
  }
  throw lastErr ?? new Error("Fetch failed after retries");
}

export interface UseFeedOptions {
  /**
   * false = don't fetch, poll or subscribe (the last state, including any
   * localStorage copy, is kept). For feeds only rendered on some lenses: each
   * poll costs the shared API host something, and the host is memory-tight.
   * Re-enabling fetches immediately. Default true.
   */
  enabled?: boolean;
}

export function useFeed<T>(path: string, pollMs: number, opts: UseFeedOptions = {}): FeedState<T> & { refetch: () => void } {
  const enabled = opts.enabled ?? true;
  const [state, setState] = useState<FeedState<T>>(() => readLocal<T>(path) ?? emptyInitial<T>());
  const inflight = useRef<AbortController | null>(null);
  const runRef = useRef<() => Promise<void>>(async () => {});
  const failCount = useRef(0);
  const statePath = useRef(path);

  useEffect(() => {
    if (statePath.current !== path) {
      statePath.current = path;
      failCount.current = 0;
      setState(readLocal<T>(path) ?? emptyInitial<T>());
    }
    runRef.current = async () => {};
    if (!enabled) return;
    let cancelled = false;

    const run = async () => {
      // A faster poll must not restart a slow request's deadline indefinitely.
      if (inflight.current && !inflight.current.signal.aborted) return;
      const ctrl = new AbortController();
      inflight.current = ctrl;
      const deadline = setTimeout(() => ctrl.abort(new DOMException("Feed request timed out — retrying on the next refresh", "TimeoutError")), 15_000);
      try {
        const sep = path.includes("?") ? "&" : "?";
        const url = `${path}${sep}_=${Date.now()}`;
        const res = await fetchWithRetry(url, ctrl.signal);
        const contentType = res.headers.get("content-type") ?? "";
        if (!contentType.toLowerCase().includes("application/json")) {
          throw new Error(`Expected JSON, received ${contentType || "unknown content type"}`);
        }
        const json = (await res.json()) as NormalizedFeed<T>;
        if (!Array.isArray(json?.features) || !json?.meta || typeof json.meta.fetchedAt !== "string") {
          throw new Error("Invalid feed response");
        }
        if (cancelled) return;
        // Older installed workers may still return cached JSON with its old LIVE tier.
        if (res.headers.get("x-sw-source") === "stale-cache") {
          json.meta.fallbackTier = "cache";
        }
        const elapsed = Math.round((Date.now() - Date.parse(json.meta.fetchedAt)) / 60_000);
        if (json.meta.ageMinutes >= 0) json.meta.ageMinutes = Math.max(json.meta.ageMinutes, Number.isFinite(elapsed) ? elapsed : 0);
        failCount.current = 0;
        setState((prev) => {
          // Short-circuit only when both observation metadata and payload match.
          // Avoids cascading re-renders of bangkokPulse, chips, tickers on every poll.
          if (
            prev.loadedAt === json.meta.fetchedAt &&
            prev.fallbackTier === json.meta.fallbackTier &&
            prev.ageMinutes === json.meta.ageMinutes &&
            prev.error === null &&
            JSON.stringify(prev.data) === JSON.stringify(json.features)
          ) {
            return prev;
          }
          const next: FeedState<T> = {
            data: json.features,
            ageMinutes: json.meta.ageMinutes,
            fallbackTier: json.meta.fallbackTier,
            loadedAt: json.meta.fetchedAt,
            error: null,
          };
          writeLocal(path, next);
          return next;
        });
      } catch (err) {
        if (cancelled || ((err as Error).name === "AbortError" && ctrl.signal.reason?.name !== "TimeoutError")) return;
        failCount.current += 1;
        const errMsg = ctrl.signal.reason?.name === "TimeoutError" ? ctrl.signal.reason.message : (err as Error).message;
        const fails = failCount.current;
        setState((prev) => {
          const tier: FallbackTier | "loading" =
            !prev.loadedAt || fails >= UNAVAILABLE_AFTER_FAILS
              ? "unavailable"
              : prev.fallbackTier === "live" ? "cache" : prev.fallbackTier;
          if (prev.error === errMsg && prev.fallbackTier === tier) return prev;
          const elapsed = prev.loadedAt ? (Date.now() - Date.parse(prev.loadedAt)) / 60_000 : 0;
          return { ...prev, error: errMsg, fallbackTier: tier,
            ageMinutes: Math.max(prev.ageMinutes, Number.isFinite(elapsed) ? elapsed : 0) };
        });
      } finally {
        clearTimeout(deadline);
        if (inflight.current === ctrl) inflight.current = null;
      }
    };
    runRef.current = run;

    run();
    const id = setInterval(run, pollMs);
    return () => {
      cancelled = true;
      runRef.current = async () => {};
      clearInterval(id);
      inflight.current?.abort();
    };
  }, [path, pollMs, enabled]);

  // Real-time push: when /api/stream says one of this path's cache keys has a
  // fresh upstream fetch, refetch now instead of waiting out pollMs. The poll
  // above stays as the fallback (stream down, proxy strips SSE, old API).
  // Paths with no mapping in lib/feedKeys.ts simply don't subscribe.
  const loadedAtRef = useRef<string | null>(state.loadedAt);
  loadedAtRef.current = state.loadedAt;
  useEffect(() => {
    if (!enabled) return;
    const keys = feedKeysForPath(path);
    if (keys.length === 0) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = subscribeFeedKeys(keys, (msg) => {
      // Our own poll already landed this exact fetch → nothing new to get.
      if (msg.event === "feed" && keys.length === 1 && msg.data.fetchedAt === loadedAtRef.current) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { timer = null; void runRef.current(); }, LIVE_REFETCH_DEBOUNCE_MS);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [path, enabled]);

  const refetch = useCallback(() => runRef.current(), []);

  // Memoize the returned object so its identity is stable across renders when
  // the underlying `state` and `refetch` haven't changed. Consumers spread
  // `feed.data` / `feed.fallbackTier` straight into the layer memo's dep
  // array — without this, every parent re-render produced a fresh feed
  // reference and rebuilt the entire deck.gl layer stack at gesture frequency.
  return useMemo(() => ({ ...state, refetch }), [state, refetch]);
}
